import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, ExternalLink, ArrowUpRight } from "lucide-react";
import { AUTHORITY_SOURCES } from "@/lib/aboutContent";
import { api, imgUrl } from "@/lib/api";
import { PressTimeline } from "@/components/PressTimeline";
import { PressCoverage } from "@/components/PressCoverage";

export default function Press() {
  const [clippings, setClippings] = useState([]);

  useEffect(() => {
    api
      .get("/public/press")
      .then((r) => {
        const items = (r.data.items || []).filter(
          (p) => !(p.source || "").toLowerCase().includes("influential")
        );
        setClippings(items);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="bg-[#050308] text-white min-h-screen">
      {/* HERO */}
      <section className="relative border-b border-white/5 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              "radial-gradient(circle at 20% 0%, rgba(232,180,196,0.18), transparent 55%)",
          }}
        />
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-12 pt-20 pb-14">
          <div className="flex items-center gap-3 mb-4">
            <BadgeCheck size={18} style={{ color: "var(--gold)" }} />
            <p className="eyebrow">Press &amp; Recognition</p>
          </div>
          <h1 className="font-serif font-light tracking-tighter text-[2.6rem] sm:text-6xl lg:text-7xl leading-[0.92]">
            Nina Capone
            <sup className="align-super text-[0.35em] tracking-normal opacity-80">™</sup>
            <br />
            <em className="italic" style={{ color: "var(--gold)" }}>
              Featured In.
            </em>
          </h1>
          <p className="mt-6 text-white/65 text-base sm:text-lg max-w-2xl leading-relaxed">
            The identity, catalog, businesses, and press behind Nina Capone —
            independently verified across music, film, media, and industry.
            Every source below corroborates the same artist: Jamilah N. Lawry,
            professionally known as Nina Capone.
          </p>
        </div>
      </section>

      {/* AS SEEN ON — INFLUENTIAL WOMEN VERIFIED BADGE */}
      <section className="max-w-[1200px] mx-auto px-6 lg:px-12 pt-16" data-testid="press-as-seen-on">
        <p className="eyebrow mb-2" style={{ color: "var(--gold)" }}>
          As Seen On
        </p>
        <h2 className="font-serif font-light tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[0.95] mb-10">
          Independently verified.
        </h2>
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <a
            href="https://influentialwomen.com/profile/jamilah-lawry"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative block max-w-md mx-auto lg:mx-0"
            data-testid="as-seen-on-badge-link"
          >
            <div
              className="absolute -inset-3 rounded-xl opacity-40 blur-2xl transition-opacity group-hover:opacity-60"
              style={{ background: "radial-gradient(circle at 50% 25%, rgba(232,180,196,0.4), transparent 70%)" }}
            />
            <img
              src={imgUrl("/api/static/press/as-seen-on-badge.png")}
              alt="Influential Women Verified — Jamilah N. Lawry (Nina Capone), As Seen On FOX, USA Today, MarketWatch, CBS"
              loading="lazy"
              className="relative w-full rounded-xl border border-white/10 shadow-2xl"
              data-testid="as-seen-on-badge"
            />
          </a>
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <BadgeCheck size={20} style={{ color: "var(--gold)" }} />
              <p className="eyebrow">Influential Women — Verified 2026</p>
            </div>
            <p className="text-white/65 leading-relaxed max-w-lg">
              Nina Capone is an <strong className="text-white/85">Influential Women</strong>
              &nbsp;verified member for 2026, with coverage seen on{" "}
              <span className="text-white/85">FOX</span>,{" "}
              <span className="text-white/85">USA Today</span>,{" "}
              <span className="text-white/85">MarketWatch</span>, and{" "}
              <span className="text-white/85">CBS</span> — recognizing her work as
              Founder &amp; CEO of My Biz Consulting LLC.
            </p>
            <a
              href="https://influentialwomen.com/profile/jamilah-lawry"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm link-gold"
              data-testid="as-seen-on-profile-link"
            >
              View the verified profile <ArrowUpRight size={15} />
            </a>
          </div>
        </div>
      </section>

      <PressCoverage />

      {clippings.length > 0 && (
        <section className="max-w-[1200px] mx-auto px-6 lg:px-12 pt-16" data-testid="press-clippings">
          <p className="eyebrow mb-2" style={{ color: "var(--gold)" }}>
            Featured Coverage
          </p>
          <h2 className="font-serif font-light tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[0.95] mb-10">
            In the press &mdash; from 1993 to today.
          </h2>
          <PressTimeline items={clippings} />
        </section>
      )}

      {/* AUTHORITY SOURCES */}
      <section className="max-w-[1200px] mx-auto px-6 lg:px-12 py-16">
        <p className="eyebrow mb-8" style={{ color: "var(--gold)" }}>
          Verified Sources
        </p>
        <div className="space-y-12">
          {AUTHORITY_SOURCES.map((grp) => (
            <div key={grp.group} data-testid="press-group">
              <p className="eyebrow mb-5" style={{ color: "var(--gold)" }}>
                {grp.group}
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {grp.items.map((it) => (
                  <a
                    key={it.name}
                    href={it.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="surface p-5 flex flex-col gap-2 group hover:border-[color:var(--gold)]/40 transition-all"
                    data-testid="press-source"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-white font-medium group-hover:text-[color:var(--gold)] transition-colors inline-flex items-center gap-2">
                        <BadgeCheck size={15} style={{ color: "var(--gold)" }} />{" "}
                        {it.name}
                      </span>
                      <span className="text-[9px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border border-white/10 text-white/50 shrink-0">
                        {it.priority}
                      </span>
                    </div>
                    <p className="text-sm text-white/55 leading-relaxed">
                      {it.note}
                    </p>
                    <span className="text-[11px] text-white/35 inline-flex items-center gap-1 mt-1">
                      <ExternalLink size={11} /> {hostname(it.url)}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* MEDIA / CTA */}
        <div className="mt-16 surface p-8 sm:p-10 text-center">
          <p className="eyebrow mb-3" style={{ color: "var(--gold)" }}>
            Media &amp; Booking
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl tracking-tighter font-light mb-4">
            Writing about Nina Capone
            <sup className="align-super text-[0.4em] tracking-normal opacity-80">™</sup>
            ?
          </h2>
          <p className="text-white/60 text-sm max-w-xl mx-auto leading-relaxed mb-7">
            For interviews, press assets, and booking inquiries, reach out
            through the official EPK &amp; booking page or read the full
            biography and discography.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/epk"
              className="btn-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
              data-testid="press-book"
            >
              Booking &amp; EPK <ArrowUpRight size={14} />
            </Link>
            <Link
              to="/about"
              className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
              data-testid="press-about"
            >
              Full Biography <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function hostname(url) {
  try {
    return new URL(url).hostname.replace("www.", "");
  } catch {
    return "";
  }
}
